<template>
	<BasePlaceHolder class="user-creation-form" :placehold="loading" :line-count="3">
		<form @submit.prevent>
			<div v-if="showNameFields">
				<div>
					<InputField :field="v$.firstName" field-name="First Name">
						<template #label>Legal first name</template>
						<template #input>
							<input
								v-model="v$.firstName.$model"
								data-test="first-name"
								name="firstName"
								class="input"
								type="text"
								autocomplete="given-name"
							/>
						</template>
					</InputField>
				</div>
				<div class="mt-150">
					<InputField :field="v$.lastName" field-name="Last Name">
						<template #label>Legal last name </template>
						<template #input>
							<input
								v-model="v$.lastName.$model"
								data-test="last-name"
								name="lastName"
								class="input"
								type="text"
								autocomplete="family-name"
							/>
						</template>
					</InputField>
				</div>
			</div>

			<InputField
				v-if="showEmailField"
				class="mb-100"
				:field="v$.email"
				field-name="Email Address"
				@clear-server="serverValidationHandler.clearServerErrors('email')"
			>
				<template #label>Email address</template>
				<template #input>
					<input v-model="v$.email.$model" data-test="email" name="email" class="input" type="email" />
				</template>
			</InputField>

			<div v-if="showPasswordField">
				<PasswordInput
					v-model="v$.password.$model"
					component-key="password"
					:force-mask="isSubmitted"
					server-error="password"
					:server-validation-handler="serverValidationHandler"
					:show-password-label="showPasswordLabel"
				/>
			</div>
			<div v-if="buttonAboveAgreements" class="display-flex-lg justify-content-flex-end-lg mb-100">
				<SubmitButton
					data-test="account-creation-submit"
					classes="button-primary submit-button-above-agreements"
					:is-working="isSubmitted"
					:v$="v$"
					@sb-click="submit"
				>
					{{ submitButtonText }}
				</SubmitButton>
			</div>
			<span v-if="showAgreementMessage" class="text-color-content-tertiary body-sm display-block">
				<span v-for="(link, index) in clientAgreementLinks" :key="`link.title${index}`">
					{{ link.prelude }}
					<BaseLink
						:link="{ target: '_blank', href: link.url, text: link.title }"
						classes="text-color-current-color text-decoration-underline"
					></BaseLink> </span
				>.
			</span>
			<div v-if="!buttonAboveAgreements">
				<BaseButtonGroup>
					<template #tertiary>
						<slot name="additionalFormCta"> </slot>
					</template>
					<template #primary>
						<SubmitButton
							class="width-100"
							data-test="account-creation-submit"
							:is-working="isSubmitted"
							:v$="v$"
							@sb-click="submit"
						>
							{{ submitButtonText }}
						</SubmitButton>
					</template>
				</BaseButtonGroup>
			</div>
		</form>
	</BasePlaceHolder>
</template>

<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { getFundriseClientAgreementDocuments } from '@api/documents';
import ServerValidationHandler from '@classes/ServerValidationHandler';
import InputField from '@components/form/input-field.vue';
import PasswordInput from '@components/form/password-input.vue';
import SubmitButton from '@components/form/submit-button.vue';
import { useAppStore } from '@stores/app';
import { useSignupStore } from '@stores/signup';
import { useUserStore } from '@stores/user';
import { getDistinctId, trackSignupEvents } from '@utils/analytics';
import { getItemFromLocalStorage, setItemInLocalStorage } from '@utils/web-storage';
import { nameRegex } from '@validators/nameRegex';
import { DocumentListItem } from 'types/documents';
import { UserSignupForm } from 'types/user';

interface UserCreationFormState {
	firstName: string;
	lastName: string;
	email: string;
	password: string;
}

interface Props {
	showNameFields?: boolean;
	showEmailField?: boolean;
	showPasswordField?: boolean;
	showAgreementMessage?: boolean;
	submitButtonText?: string;
	showPasswordLabel?: boolean;
	buttonAboveAgreements?: boolean;
}

interface Emits {
	(e: 'userSubmitted'): void;
}

const appStore = useAppStore();
const signupStore = useSignupStore();
const userStore = useUserStore();

const props = withDefaults(defineProps<Props>(), {
	showNameFields: false,
	showEmailField: false,
	showPasswordField: false,
	showAgreementMessage: false,
	submitButtonText: 'Continue',
	showPasswordLabel: true,
	buttonAboveAgreements: false
});

const emits = defineEmits<Emits>();

const serverValidationHandler = ref(new ServerValidationHandler());

const rules = computed(() => {
	const validations = {
		firstName: {},
		lastName: {},
		email: {},
		password: {}
	};

	if (props.showNameFields) {
		validations.firstName = {
			required,
			nameRegex
		};
		validations.lastName = {
			required,
			nameRegex
		};
	}
	if (props.showEmailField) {
		validations.email = {
			required,
			email,
			serverError: serverValidationHandler.value.serverError('email')
		};
	}

	if (props.showPasswordField) {
		// All password validations are handled in the PasswordInput component
		validations.password = {
			required
		};
	}

	return validations;
});

const state: UserCreationFormState = reactive({
	firstName: '',
	lastName: '',
	email: '',
	password: ''
});

const v$ = useVuelidate(rules, state);

const router = useRouter();

const clientAgreementLinks = ref<Array<DocumentListItem & { prelude: string }>>([]);

const loading = ref(true);
const isSubmitted = ref(false);

const userSignupForm = ref<UserSignupForm>({
	clientId: `${import.meta.env.VITE_API_CLIENT_ID}`,
	email: getItemFromLocalStorage('emailcapture') || signupStore.email || '',
	mixpanelId: signupStore.emailCaptureFields.mixpanelId,
	password: ''
});

onMounted(() => {
	if (props.showEmailField && getItemFromLocalStorage('emailcapture')) {
		state.email = getItemFromLocalStorage('emailcapture') || '';
		v$.value.email.$touch();
	}
});

async function submit(): Promise<void> {
	isSubmitted.value = true;

	try {
		if (appStore.isAuthenticated) {
			await updateUser();
		} else {
			await attemptUserCreation();
		}
	} catch {}
}

async function constructUserForm(): Promise<UserSignupForm> {
	const userForm = { ...userSignupForm.value };

	if (props.showNameFields) {
		userForm.firstName = state.firstName;
		userForm.lastName = state.lastName;
	}

	if (props.showEmailField) {
		userForm.email = state.email;
	}

	if (props.showPasswordField) {
		userForm.password = state.password;
	}

	if (!userForm.mixpanelId) {
		userForm.mixpanelId = getDistinctId() ?? '';
	}

	return userForm;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function attemptUserCreation(): Promise<any> {
	const userForm = await constructUserForm();

	const error = await userStore.createUser({ userSignupForm: userForm });

	if (error) {
		if (error.response?.status === 405) {
			return error;
		} else {
			serverValidationHandler.value.handleServerValidation(error);
			isSubmitted.value = false;
			if (error.response?.data?.fieldErrorMap?.email) {
				await router.push({ name: 'login' });
			}
		}
	} else {
		trackSignupEvents();
		if (state.email) {
			setItemInLocalStorage('emailcapture', state.email);
		}
		afterSubmitSuccess();
	}
}

async function updateUser(): Promise<void> {
	if (props.showNameFields) {
		await userStore.setFirstAndLastNames({ firstName: state.firstName, lastName: state.lastName });
	} else {
		await userStore.getUser();
	}
	afterSubmitSuccess();
}

function afterSubmitSuccess(): void {
	emits('userSubmitted');
}

async function setup(): Promise<void> {
	const clientAgreementDocuments = await getFundriseClientAgreementDocuments();
	userSignupForm.value.privacyPolicyAcknowledgedVersionId = clientAgreementDocuments.privacyPolicy.documentRevisionId;
	userSignupForm.value.termsAcknowledgedVersionId = clientAgreementDocuments.termsOfService.documentRevisionId;

	const agreementIntro = 'By continuing you indicate you have reviewed and agree to the ';
	const privacyPolicyPrelude = ' and ';
	clientAgreementLinks.value = [
		{
			prelude: agreementIntro,
			...clientAgreementDocuments.termsOfService
		},
		{ prelude: privacyPolicyPrelude, ...clientAgreementDocuments.privacyPolicy }
	];

	state.firstName = userStore.suggestedFirstName;
	state.lastName = userStore.suggestedLastName;

	loading.value = false;
}

setup();
</script>
<style lang="scss" scoped>
@use '../../styles/constants/layout';
@use '../../styles/constants/colors' as *;

.user-creation-form {
	&__password-icon {
		right: 13px;

		&:hover,
		&:active {
			cursor: pointer;
		}
	}

	.submit-button-above-agreements {
		@media screen and (max-width: layout.$lg-start - 1) {
			width: 100%;
		}
	}
}
</style>
